'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react'
import { BRAND } from '@/components/marketing/MarketingChrome'
import { INSTALL_URL } from '@/lib/seo'


// ── Origins · interactive timeline chapters ──────────────────────────
// Each chapter is a clickable phase of the team's history. Body copy is
// the same paragraph that used to be three stacked <p>s; splitting it lets
// the reader scan one beat at a time instead of facing a wall of text.

const CHAPTERS = [
  {
    pill: '01',
    label: 'Print shop',
    period: 'Where we started',
    body: "Our team didn't start in software. We started in print. Inks, plates, substrates, the whole thing.",
  },
  {
    pill: '02',
    label: 'Agency',
    period: 'Where we grew',
    body: "Personalisation grew from a side job into our main line of work. Over the years we worked on online personalisation programmes for global brands across food, drink, beauty, fashion and sport. Names you'd find on the Fortune 500 and at luxury counters. A name and message engraved on a perfume bottle. A personalised tin of beans or ketchup. A chocolate tin with a name engraved on the lid. A pair of pyjamas printed with a name. A sports jersey with a name and number on the back. We knew the web side of those projects because we already understood the print side.",
  },
  {
    pill: '03',
    label: 'App',
    period: 'Where we are now',
    body: "Years of holding both ends of the pipeline taught us where most personalisation tools fall apart. YesPersonalized is what we built once we'd seen them all.",
  },
]

// ── Lessons · interactive selector ──────────────────────────────────
// Six numbered lessons. The selector pattern collapses the previous 6-card
// grid into a vertical title list + a single detail panel that swaps on
// hover/click. Big watermark number gives each lesson its own moment.

const LESSONS = [
  {
    n: '01',
    title: 'Customers want a live preview.',
    body: 'Not a confirmation at checkout. Live, on the product page, while they type. Drop-off on blind personalisation forms is brutal.',
  },
  {
    n: '02',
    title: "Artwork can't wait for a human.",
    body: 'Manual print-file prep is the ceiling on every personalisation business. The render has to fire the moment the order lands. Same template the customer was previewing, ready for the printer.',
  },
  {
    n: '03',
    title: 'Stores want fewer manual steps, not a creative tool.',
    body: "Every minute spent fixing a customer’s typo or re-cropping a photo is a minute not spent shipping.",
  },
  {
    n: '04',
    title: 'Most rendering tools charge before you ship.',
    body: 'Illustrator and InDesign templating engines need a server farm humming 24/7. Cloud render APIs come with per-call fees or six-figure capex up front. You pay long before the first personalised order ships.',
  },
  {
    n: '05',
    title: 'Some tools deserve to stay in the 90s.',
    body: 'Plenty of legacy variable-data engines still get sold to print-on-demand merchants. Capable of anything, designed for nobody. The learning curve alone kills weeks.',
  },
  {
    n: '06',
    title: 'Most Shopify apps stop at the preview.',
    body: 'They put a personalisation widget on the product page and call it done. The print file is still on your shoulders. You render, crop and export every order by hand.',
  },
]

const BENEFITS = [
  'A live widget on the product page',
  'A real visual editor in the admin',
  'Print-ready artwork the moment an order lands',
  'Native Shopify install, native Shopify billing',
  'No theme code to paste, no servers to run',
]

export default function AboutPage() {
  const [chapter, setChapter] = useState(0)
  const [lesson, setLesson] = useState(0)

  const cur = CHAPTERS[chapter]
  const curLesson = LESSONS[lesson]

  // Mobile carousel: track which lesson card is currently visible so the
  // dot indicator below the scroller stays in sync with the user's swipes.
  // The observer is harmless on desktop because the scroller is display:none
  // there (intersection ratio stays 0).
  const mobileScrollerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const scroller = mobileScrollerRef.current
    if (!scroller) return
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the highest visibility ratio above 0.55. That
        // threshold ignores the cards bleeding in from the next/previous slot
        // and only fires once a card has settled into the viewport centre.
        let best: IntersectionObserverEntry | null = null
        for (const e of entries) {
          if (e.intersectionRatio < 0.55) continue
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e
        }
        if (best) {
          const idx = Number((best.target as HTMLElement).dataset.idx)
          if (!Number.isNaN(idx)) setLesson(idx)
        }
      },
      { root: scroller, threshold: [0, 0.4, 0.55, 0.7, 0.9, 1] }
    )
    Array.from(scroller.children).forEach((c) => observer.observe(c))
    return () => observer.disconnect()
  }, [])

  const scrollMobileToLesson = (idx: number) => {
    const scroller = mobileScrollerRef.current
    if (!scroller) return
    const card = scroller.children[idx] as HTMLElement | undefined
    if (!card) return
    scroller.scrollTo({
      left: card.offsetLeft - (scroller.clientWidth - card.clientWidth) / 2,
      behavior: 'smooth',
    })
  }

  return (
    <>
      {/* ── Inline keyframes for content swaps ─────────────────────── */}
      <style>{`
        @keyframes ypFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ypBarGrow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes ypUnderline {
          from { width: 0; }
          to   { width: 100%; }
        }
        .yp-fade { animation: ypFadeIn 280ms ease both; }
        .yp-proof-bar > span {
          transform-origin: left center;
          animation: ypBarGrow 700ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
        }
        .yp-proof-bar > span:nth-child(2) { animation-delay: 70ms; }
        .yp-proof-bar > span:nth-child(3) { animation-delay: 140ms; }
        .yp-proof-bar > span:nth-child(4) { animation-delay: 210ms; }
        .yp-no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section
        className="saas-hero"
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${BRAND.rule}`,
          background: '#fff',
        }}
      >
        {/* CMYK-ish brand strip — print-press wink */}
        <div
          className="yp-proof-bar"
          aria-hidden
          style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            height: 5,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
          }}
        >
          <span style={{ background: BRAND.coral }} />
          <span style={{ background: BRAND.indigo }} />
          <span style={{ background: BRAND.teal }} />
          <span style={{ background: BRAND.gold }} />
        </div>

        <div className="saas-hero-grid" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

        {/* Registration crosshair — top-right corner */}
        <RegistrationMark
          style={{ position: 'absolute', top: 32, right: 32, opacity: 0.55, pointerEvents: 'none' }}
        />

        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 pt-24 pb-16 md:pt-28 md:pb-24 relative">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontSize: 10,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: BRAND.coral,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Issue 01 · Origin
                </span>
                <span style={{ height: 1, flex: 1, background: BRAND.rule }} />
              </div>

              <h1
                className="font-display"
                style={{
                  fontWeight: 700,
                  fontSize: 'clamp(40px, 8vw, 80px)',
                  lineHeight: 0.98,
                  color: BRAND.ink,
                }}
              >
                Built by printers<br />
                <span style={{ color: BRAND.indigo, position: 'relative', display: 'inline-block' }}>
                  who learned the web.
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: -2,
                      height: 4,
                      background: BRAND.coral,
                      animation: 'ypUnderline 900ms cubic-bezier(0.22, 0.61, 0.36, 1) 250ms both',
                      borderRadius: 2,
                    }}
                  />
                </span>
              </h1>
            </div>

            <div className="md:col-span-5">
              <p style={{ fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.6, color: BRAND.muted }}>
                Long before YesPersonalized existed, our team spent years putting live personalisation
                into the online stores of household-name food, drink and fragrance brands. YesPersonalized
                is that same playbook, packed into a Shopify app any store can install.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Origins · interactive chapter switcher ─────────────────── */}
      <section className="saas-section" style={{ background: '#fff' }}>
        <div className="max-w-[1100px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 mb-10">
            <div className="md:col-span-5">
              <span className="saas-eyebrow">01 · Before YesPersonalized</span>
              <h2
                className="font-display"
                style={{
                  fontWeight: 700,
                  fontSize: 'clamp(28px, 3.4vw, 44px)',
                  lineHeight: 1.05,
                  marginTop: 18,
                }}
              >
                A print shop, then an agency, now an app.
              </h2>
            </div>
            <div className="md:col-span-7 flex items-end">
              <p style={{ fontSize: 16, lineHeight: 1.6, color: BRAND.muted }}>
                Three chapters of the same building. Click each to read.
              </p>
            </div>
          </div>

          {/* Chapter selector card */}
          <div
            style={{
              position: 'relative',
              background: BRAND.gray50,
              border: `1px solid ${BRAND.rule}`,
              borderRadius: 22,
              padding: '24px',
              overflow: 'hidden',
            }}
          >
            {/* Decorative chapter index in the corner */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: 22, right: 28,
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 11, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: BRAND.mutedSoft,
                fontWeight: 600,
              }}
            >
              Chapter {chapter + 1} of {CHAPTERS.length}
            </div>

            {/* Tab pills */}
            <div
              role="tablist"
              aria-label="Chapter selector"
              className="grid sm:grid-cols-3 gap-2 sm:gap-3"
              style={{ marginTop: 24 }}
            >
              {CHAPTERS.map((c, idx) => {
                const active = chapter === idx
                return (
                  <button
                    key={c.pill}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setChapter(idx)}
                    style={{
                      position: 'relative',
                      background: active ? '#fff' : 'transparent',
                      border: `1px solid ${active ? BRAND.indigo : BRAND.rule}`,
                      borderRadius: 14,
                      padding: '16px 18px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 200ms ease',
                      boxShadow: active ? '0 6px 20px -8px rgba(67, 87, 173, 0.4)' : 'none',
                      transform: active ? 'translateY(-1px)' : 'none',
                      fontFamily: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.borderColor = BRAND.gray300
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.borderColor = BRAND.rule
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        fontSize: 10,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: active ? BRAND.indigo : BRAND.mutedSoft,
                        fontWeight: 600,
                        marginBottom: 6,
                      }}
                    >
                      {c.pill} · {c.period}
                    </div>
                    <div
                      className="font-display"
                      style={{
                        fontWeight: 700,
                        fontSize: 20,
                        color: active ? BRAND.ink : BRAND.gray700,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                      }}
                    >
                      {c.label}
                    </div>
                    {/* coral active mark — bottom edge */}
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        bottom: -1, left: 14, right: 14,
                        height: 2,
                        background: active ? BRAND.coral : 'transparent',
                        borderRadius: 999,
                        transition: 'background 200ms ease',
                      }}
                    />
                  </button>
                )
              })}
            </div>

            {/* Active body */}
            <div
              key={chapter}
              className="yp-fade"
              style={{
                marginTop: 28,
                padding: '4px 4px 12px',
                fontSize: 17,
                lineHeight: 1.7,
                color: BRAND.ink2,
              }}
            >
              {cur.body}
            </div>
          </div>
        </div>
      </section>

      {/* ── Lessons · interactive selector ─────────────────────────── */}
      <section
        className="saas-section"
        style={{ background: BRAND.gray50, borderTop: `1px solid ${BRAND.rule}` }}
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 mb-12">
            <div className="md:col-span-5">
              <span className="saas-eyebrow">02 · What we learned</span>
              <h2
                className="font-display"
                style={{
                  fontWeight: 700,
                  fontSize: 'clamp(32px, 4vw, 52px)',
                  lineHeight: 1.02,
                  marginTop: 18,
                }}
              >
                Six things, mostly.
              </h2>
            </div>
            <div className="md:col-span-7">
              <p style={{ fontSize: 17, lineHeight: 1.6, color: BRAND.muted }}>
                Years of holding both ends of the pipeline boiled down to a short list. YesPersonalized
                is the app we wished existed when we were building these systems by hand.
              </p>
            </div>
          </div>

          {/* MOBILE · horizontal swipe carousel ─────────────────────────
            * Scroll-snap full-width cards. IntersectionObserver above keeps
            * the dot indicator in sync with whichever card the user has
            * swiped to. Hidden at md+ where the desktop selector pattern
            * takes over. */}
          <div className="md:hidden">
            <div
              ref={mobileScrollerRef}
              className="yp-no-scrollbar"
              style={{
                display: 'flex',
                gap: 14,
                overflowX: 'auto',
                paddingBottom: 8,
                paddingLeft: 4,
                paddingRight: 4,
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {LESSONS.map((l, idx) => (
                <article
                  key={l.n}
                  data-idx={idx}
                  style={{
                    flex: '0 0 88%',
                    maxWidth: 380,
                    scrollSnapAlign: 'center',
                    scrollSnapStop: 'always',
                    background: '#fff',
                    border: `1px solid ${BRAND.rule}`,
                    borderRadius: 18,
                    padding: '28px 22px 26px',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 280,
                  }}
                >
                  {/* Watermark number */}
                  <span
                    aria-hidden
                    className="font-display"
                    style={{
                      position: 'absolute',
                      top: -22, right: -8,
                      fontWeight: 800,
                      fontSize: 160,
                      lineHeight: 0.85,
                      color: BRAND.coral,
                      opacity: 0.07,
                      letterSpacing: '-0.05em',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    {l.n}
                  </span>

                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <span
                      style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        fontSize: 11,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: BRAND.coral,
                        fontWeight: 700,
                      }}
                    >
                      Lesson {l.n}
                    </span>
                    <h3
                      className="font-display"
                      style={{
                        fontWeight: 700,
                        fontSize: 22,
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        marginTop: 12,
                        marginBottom: 14,
                        color: BRAND.ink,
                      }}
                    >
                      {l.title}
                    </h3>
                    <p style={{ fontSize: 15, lineHeight: 1.65, color: BRAND.ink2 }}>
                      {l.body}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            {/* Tap-to-jump dots */}
            <div
              role="tablist"
              aria-label="Lesson position"
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 8,
                marginTop: 20,
              }}
            >
              {LESSONS.map((_, idx) => {
                const active = lesson === idx
                return (
                  <button
                    key={idx}
                    role="tab"
                    aria-label={`Lesson ${idx + 1}`}
                    aria-selected={active}
                    onClick={() => scrollMobileToLesson(idx)}
                    style={{
                      width: active ? 26 : 8,
                      height: 8,
                      borderRadius: 999,
                      background: active ? BRAND.coral : BRAND.gray300,
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      transition: 'all 200ms ease',
                    }}
                  />
                )
              })}
            </div>

            {/* Swipe hint — fades in once on mount, doesn't move */}
            <div
              style={{
                textAlign: 'center',
                marginTop: 14,
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: BRAND.mutedSoft,
                fontWeight: 600,
              }}
            >
              Swipe to read more
            </div>
          </div>

          {/* DESKTOP · numbered selector + sticky detail panel ───────── */}
          <div className="hidden md:grid md:grid-cols-12 gap-5 md:gap-8">
            {/* Left · numbered title list */}
            <div className="md:col-span-5 flex flex-col gap-1">
              {LESSONS.map((l, idx) => {
                const active = lesson === idx
                return (
                  <button
                    key={l.n}
                    onClick={() => setLesson(idx)}
                    onMouseEnter={() => setLesson(idx)}
                    style={{
                      position: 'relative',
                      textAlign: 'left',
                      padding: '16px 18px 16px 26px',
                      background: active ? '#fff' : 'transparent',
                      border: `1px solid ${active ? BRAND.rule : 'transparent'}`,
                      borderRadius: 12,
                      cursor: 'pointer',
                      transition: 'all 180ms ease',
                      fontFamily: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
                    }}
                  >
                    {/* coral active marker (left edge) */}
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: active ? 4 : 2,
                        height: active ? 30 : 16,
                        background: active ? BRAND.coral : BRAND.gray300,
                        borderRadius: 999,
                        transition: 'all 200ms ease',
                      }}
                    />
                    <div className="flex items-baseline gap-3">
                      <span
                        style={{
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontSize: 11,
                          letterSpacing: '0.2em',
                          color: active ? BRAND.coral : BRAND.mutedSoft,
                          fontWeight: 700,
                          width: 22,
                          flexShrink: 0,
                        }}
                      >
                        {l.n}
                      </span>
                      <span
                        className="font-display"
                        style={{
                          fontWeight: active ? 700 : 500,
                          fontSize: 16,
                          color: active ? BRAND.ink : BRAND.gray700,
                          lineHeight: 1.35,
                          letterSpacing: '-0.012em',
                          transition: 'all 180ms ease',
                        }}
                      >
                        {l.title}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Right · sticky detail panel */}
            <div className="md:col-span-7">
              <div
                style={{
                  position: 'sticky',
                  top: 88,
                  background: '#fff',
                  border: `1px solid ${BRAND.rule}`,
                  borderRadius: 22,
                  padding: '40px 36px 36px',
                  minHeight: 360,
                  overflow: 'hidden',
                }}
              >
                {/* Big ghosted lesson number — typographic watermark */}
                <span
                  aria-hidden
                  className="font-display"
                  key={`watermark-${lesson}`}
                  style={{
                    position: 'absolute',
                    top: -38,
                    right: -12,
                    fontWeight: 800,
                    fontSize: 'clamp(180px, 22vw, 260px)',
                    lineHeight: 0.85,
                    color: BRAND.coral,
                    opacity: 0.07,
                    letterSpacing: '-0.05em',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    animation: 'ypFadeIn 360ms ease both',
                  }}
                >
                  {curLesson.n}
                </span>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <span
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontSize: 11,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: BRAND.coral,
                      fontWeight: 700,
                    }}
                  >
                    Lesson {curLesson.n}
                  </span>

                  <h3
                    key={`title-${lesson}`}
                    className="font-display yp-fade"
                    style={{
                      fontWeight: 700,
                      fontSize: 'clamp(24px, 2.6vw, 32px)',
                      lineHeight: 1.18,
                      letterSpacing: '-0.025em',
                      marginTop: 14,
                      marginBottom: 18,
                      color: BRAND.ink,
                    }}
                  >
                    {curLesson.title}
                  </h3>

                  <p
                    key={`body-${lesson}`}
                    className="yp-fade"
                    style={{
                      fontSize: 16,
                      lineHeight: 1.7,
                      color: BRAND.ink2,
                      animationDelay: '60ms',
                    }}
                  >
                    {curLesson.body}
                  </p>

                  {/* Tiny pagination dots — visual feedback for selection */}
                  <div
                    aria-hidden
                    style={{
                      display: 'flex',
                      gap: 6,
                      marginTop: 32,
                      paddingTop: 20,
                      borderTop: `1px dashed ${BRAND.rule}`,
                    }}
                  >
                    {LESSONS.map((_, idx) => (
                      <span
                        key={idx}
                        style={{
                          width: lesson === idx ? 18 : 6,
                          height: 6,
                          borderRadius: 999,
                          background: lesson === idx ? BRAND.coral : BRAND.gray300,
                          transition: 'all 200ms ease',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission · benefit cards ────────────────────────────────── */}
      <section
        className="saas-section"
        style={{ background: '#fff', borderTop: `1px solid ${BRAND.rule}` }}
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <span className="saas-eyebrow">03 · Who it&apos;s for</span>
              <h2
                className="font-display"
                style={{
                  fontWeight: 700,
                  fontSize: 'clamp(32px, 4vw, 52px)',
                  lineHeight: 1.02,
                  marginTop: 18,
                  marginBottom: 20,
                }}
              >
                Same playbook.<br />
                <span style={{ color: BRAND.indigo }}>Smaller invoice.</span>
              </h2>
              <p style={{ fontSize: 16, lineHeight: 1.65, color: BRAND.muted }}>
                Every Shopify store doing print-on-demand or personalisation deserves what the
                global brands have. Without the six-figure agency engagement they paid for.
              </p>
            </div>

            <div className="md:col-span-7">
              <div className="grid sm:grid-cols-2 gap-3">
                {BENEFITS.map((line, idx) => {
                  const isLast = idx === BENEFITS.length - 1
                  return (
                    <BenefitCard key={line} line={line} fullWidth={isLast} />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────── */}
      <section
        className="saas-section"
        style={{
          background: BRAND.gray50,
          borderTop: `1px solid ${BRAND.rule}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Bottom CMYK strip — bookends the page */}
        <div
          aria-hidden
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: 5,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
          }}
        >
          <span style={{ background: BRAND.gold }} />
          <span style={{ background: BRAND.teal }} />
          <span style={{ background: BRAND.indigo }} />
          <span style={{ background: BRAND.coral }} />
        </div>

        <div className="max-w-[900px] mx-auto text-center relative">
          <h2
            className="font-display"
            style={{
              fontWeight: 700,
              fontSize: 'clamp(36px, 6vw, 64px)',
              lineHeight: 1.02,
              marginBottom: 18,
            }}
          >
            Bring the template idea.<br />
            <span style={{ color: BRAND.indigo }}>We&apos;ll handle the rest.</span>
          </h2>
          <p
            style={{
              fontSize: 17, lineHeight: 1.55, color: BRAND.muted,
              maxWidth: 540, margin: '0 auto 32px',
            }}
          >
            Install YesPersonalized from the Shopify App Store. Build your first template in minutes.
            No card to install.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={INSTALL_URL} className="btn-primary" style={{ padding: '15px 26px', fontSize: 15 }}>
              Install on Shopify
              <ArrowRight size={16} strokeWidth={2.5} />
            </a>
            <Link href="/docs" className="btn-secondary" style={{ padding: '14px 24px', fontSize: 15 }}>
              Read the docs
              <ArrowUpRight size={15} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}

// ── Benefit card with hover lift ──────────────────────────────────────

function BenefitCard({ line, fullWidth }: { line: string; fullWidth?: boolean }) {
  const [hover, setHover] = useState(false)
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={fullWidth ? 'sm:col-span-2' : ''}
      style={{
        position: 'relative',
        padding: '18px 18px 18px 56px',
        background: '#fff',
        border: `1px solid ${hover ? BRAND.gray300 : BRAND.rule}`,
        borderRadius: 14,
        transition: 'all 180ms ease',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 6px 18px -10px rgba(20,20,20,0.18)' : 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 18,
          top: 16,
          width: 28,
          height: 28,
          borderRadius: 999,
          background: 'rgba(72, 169, 166, 0.12)',
          color: '#48A9A6',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 200ms ease',
          transform: hover ? 'scale(1.08) rotate(-6deg)' : 'none',
        }}
      >
        <Check size={14} strokeWidth={3} />
      </span>
      <span style={{ fontSize: 16, color: BRAND.ink2, lineHeight: 1.4 }}>
        {line}
      </span>
    </div>
  )
}

// ── Registration crosshair (decorative print-proof mark) ─────────────

function RegistrationMark({ style }: { style?: React.CSSProperties }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" style={style} aria-hidden focusable="false">
      <circle cx="11" cy="11" r="6" fill="none" stroke={BRAND.gray400} strokeWidth="0.7" />
      <line x1="11" y1="0" x2="11" y2="22" stroke={BRAND.gray400} strokeWidth="0.7" />
      <line x1="0" y1="11" x2="22" y2="11" stroke={BRAND.gray400} strokeWidth="0.7" />
    </svg>
  )
}
